import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { Portfolio } from "@/components/sections/portfolio";
import { CTABanner } from "@/components/sections/cta-banner";
import {
  Star, FileText, MessageSquareQuote, ShoppingCart, Stethoscope, Banknote, ArrowRight,
} from "lucide-react";

export const Route = createFileRoute("/portfolio/")({
  component: PortfolioIndex,
  head: () => ({
    meta: [
      { title: "Portfolio — CoderLity" },
      { name: "description", content: "Selected case studies from CoderLity across SaaS, ERP, mobile, marketing and AI." },
      { property: "og:title", content: "Portfolio — CoderLity" },
      { property: "og:description", content: "Selected case studies from CoderLity across SaaS, ERP, mobile, marketing and AI." },
      { property: "og:url", content: "/portfolio" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/portfolio" }],
  }),
});

type Item = { slug: string; title: string; desc: string; icon: typeof Star };
const work: Item[] = [
  { slug: "featured-projects", title: "Featured Projects", desc: "Explore featured projects", icon: Star },
  { slug: "case-studies", title: "Case Studies", desc: "Explore case studies", icon: FileText },
  { slug: "client-testimonials", title: "Client Testimonials", desc: "Explore client testimonials", icon: MessageSquareQuote },
];
const industries: Item[] = [
  { slug: "ecommerce", title: "E-commerce", desc: "Explore e-commerce", icon: ShoppingCart },
  { slug: "healthcare", title: "Healthcare", desc: "Explore healthcare", icon: Stethoscope },
  { slug: "fintech", title: "Fintech", desc: "Explore fintech", icon: Banknote },
];

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.slug}
                to="/portfolio/$child"
                params={{ child: it.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PortfolioIndex() {
  return (
    <PageShell>
      <PageHero eyebrow="Portfolio" title="Work we're proud of" sub="A handful of recent projects across industries and continents." />
      <Portfolio />
      <Group title="Work" items={work} />
      <Group title="Industries" items={industries} />
      <CTABanner />
    </PageShell>
  );
}
